import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, mediaUrl, reelThumbnail } from "../lib/api";
import { StreamHLSPlayer } from "../components/CMSPanel";
import { ArrowLeft, ShareNetwork, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Per-reel SEO detail page.
 *
 * Every reel gets its own URL (`/reels/:slug`) with a shareable/scrapeable
 * title + description injected into <head>, a full-width player that respects
 * portrait/landscape via Cloudflare Stream's responsive component, and a
 * related-reels strip so viewers keep browsing.
 *
 * Backend routes:
 *   GET /api/reels/:slug   → the reel
 *   GET /api/reels         → sibling list for "related reels"
 */
export default function ReelDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [reel, setReel] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    setReel(null);
    api.get(`/reels/${slug}`).then(({ data }) => setReel(data)).catch(() => setNotFound(true));
    api.get("/reels").then(({ data }) => setRelated(data)).catch(() => {});
  }, [slug]);

  // Update <head> for social preview + browser tab. This is a SPA — Google's
  // crawler executes JS, so this gives us usable metadata even without SSR.
  useEffect(() => {
    if (!reel) return;
    const desc = reel.seo_description || reel.what_it_solves || `${reel.category} reel from Wisewit AI.`;
    document.title = `${reel.title} · Wisewit AI Reel`;
    setMeta("description", desc);
    setMeta("og:title", `${reel.title} · Wisewit AI`, true);
    setMeta("og:description", desc, true);
    if (reel.video_url || reel.cover_image) setMeta("og:image", reelThumbnail(reel), true);
    setMeta("og:type", "video.other", true);
    setMeta("twitter:card", "summary_large_image");
    return () => { document.title = "Wisewit — AI systems for industrial execution"; };
  }, [reel]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: reel?.title, text: reel?.what_it_solves, url }); return; } catch { /* user cancelled */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success("Reel URL copied to clipboard."); }
    catch { toast.error("Couldn't copy — the URL is in the address bar."); }
  };

  if (notFound) {
    return (
      <div className="px-6 md:px-14 py-24 md:py-32" data-testid="reel-not-found">
        <div className="text-overline mb-4">◢ Not found</div>
        <h1 className="font-display text-4xl text-white">This reel doesn't exist.</h1>
        <p className="mt-3 text-[#8E8E93]">It may have been removed or the URL is mistyped.</p>
        <Link to="/reels" className="btn-primary mt-8 inline-flex"><ArrowLeft size={14} /> Back to Reel library</Link>
      </div>
    );
  }

  if (!reel) return <div className="px-6 md:px-14 py-24 text-[#8E8E93]" data-testid="reel-loading">Loading reel…</div>;

  const isStream = reel.video_url && (/\.m3u8(\?|#|$)/i.test(reel.video_url) || /cloudflarestream\.com/i.test(reel.video_url));
  const others = related.filter((r) => r.id !== reel.id).slice(0, 6);

  return (
    <div data-testid="reel-detail-page" className="px-6 md:px-14 py-12 md:py-20">
      <Link to="/reels" className="inline-flex items-center gap-2 text-[#8E8E93] hover:text-white text-sm mb-8 font-mono tracking-[0.16em] uppercase text-[10px]">
        <ArrowLeft size={12} /> Back to reels
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 max-w-6xl">
        <div className="lg:col-span-2">
          <div className="bg-[#050505] border border-[#2A2A2A] flex items-center justify-center max-h-[75vh]" data-testid="reel-detail-player">
            {reel.video_url ? (
              isStream ? (
                <div className="w-full"><StreamHLSPlayer src={reel.video_url} poster={reelThumbnail(reel)} /></div>
              ) : (
                <video
                  src={mediaUrl(reel.video_url)}
                  poster={reelThumbnail(reel)}
                  controls
                  playsInline
                  className="max-h-[75vh] max-w-full w-auto h-auto object-contain"
                />
              )
            ) : (
              <img src={reelThumbnail(reel)} alt={reel.title} className="max-h-[75vh] max-w-full w-auto h-auto object-contain" />
            )}
          </div>
        </div>

        <aside className="space-y-5">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">◢ {reel.category}</div>
          <h1 className="font-display text-4xl md:text-5xl text-white leading-[1.05] tracking-tighter">{reel.title}</h1>
          <p className="text-[#D7D7D9] text-[15px] leading-relaxed">{reel.what_it_solves}</p>
          {reel.tech?.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="reel-detail-tech">
              {reel.tech.map((t) => <span key={t} className="ww-chip">{t}</span>)}
            </div>
          )}
          <div className="flex items-center gap-3 pt-2 border-t border-[#2A2A2A]">
            <button onClick={share} className="btn-secondary" data-testid="reel-share-btn"><ShareNetwork size={14} /> Share</button>
            <span className="font-mono text-[10px] text-[#8E8E93]">{reel.duration_sec}s</span>
          </div>
          <div className="pt-4">
            <Link to="/contact" data-testid="reel-detail-cta" className="btn-primary w-full">
              Build this for your team <ArrowRight size={14} />
            </Link>
          </div>
        </aside>
      </div>

      {others.length > 0 && (
        <section className="mt-20 max-w-6xl" data-testid="related-reels">
          <div className="text-overline mb-6">◢ More reels</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
            {others.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/reels/${r.slug || r.id}`)}
                data-testid={`related-reel-${r.id}`}
                className="aspect-[16/10] relative overflow-hidden bg-[#050505] group text-left"
              >
                <img src={reelThumbnail(r)} alt={r.title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#DFFF00]">{r.category}</div>
                  <div className="mt-1 font-display text-sm text-white line-clamp-2">{r.title}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function setMeta(nameOrProp, value, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${nameOrProp}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, nameOrProp);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}
